import {
  AlertTriangle,
  CheckCircle,
  AlertCircle,
  Stethoscope,
  ChevronDown,
  ChevronUp,
  XCircle,
  Lightbulb,
  Clock,
  Shield,
  Target,
  Pill,
  Brain,
} from 'lucide-react'
import { useState, useMemo } from 'react'
import ReactMarkdown from 'react-markdown'
import type { AIAnalysisResponse, Severity } from '../types'
import { RecommendationsList } from './RecommendationsList'
import { Timeline } from './Timeline'

// Extract chain_of_thought from raw JSON content
function extractChainOfThought(rawContent: string | undefined): string | null {
  if (!rawContent) return null

  const cleaned = rawContent.trim()
  if (!cleaned.startsWith('{')) return null

  try {
    const parsed = JSON.parse(cleaned)
    return parsed.chain_of_thought || null
  } catch {
    // Try to extract from partial JSON
    const startMarker = '"chain_of_thought":"'
    const startIdx = cleaned.indexOf(startMarker)
    if (startIdx !== -1) {
      const valueStart = startIdx + startMarker.length
      let result = ''
      let i = valueStart

      while (i < cleaned.length) {
        if (cleaned[i] === '\\' && i + 1 < cleaned.length) {
          const nextChar = cleaned[i + 1]
          if (nextChar === 'n') result += '\n'
          else if (nextChar === 't') result += '\t'
          else if (nextChar === '"') result += '"'
          else if (nextChar === '\\') result += '\\'
          else result += cleaned[i + 1]
          i += 2
        } else if (cleaned[i] === '"') {
          break
        } else {
          result += cleaned[i]
          i++
        }
      }
      return result || null
    }
  }
  return null
}

// Extract key information from the AI summary for the abstract
function extractAbstract(summary: string): {
  diagnoses: Array<{ name: string; confidence: string; evidence: string[] }>;
  treatment: string[];
} {
  const diagnoses: Array<{ name: string; confidence: string; evidence: string[] }> = []
  const treatment: string[] = []

  // Extract differential diagnoses section
  const diagnosisMatch = summary.match(/6\.\s*DIFFERENTIAL DIAGNOSIS[\s\S]*?(?=###\s*7|$)/i)
  if (diagnosisMatch) {
    const diagSection = diagnosisMatch[0]
    const diagBlocks = diagSection.split(/\d+\.\s*\*\*/).filter(Boolean)

    for (const block of diagBlocks) {
      const nameMatch = block.match(/^([^*]+)\*\*\s*[-–]\s*(\d+%?\s*[Cc]onfidence|\d+%)/)
      if (nameMatch) {
        const evidence: string[] = []
        const supportMatch = block.match(/Supporting[^:]*:\s*([^\n]+)/i)
        if (supportMatch) {
          evidence.push(supportMatch[1].trim())
        }
        diagnoses.push({
          name: nameMatch[1].trim(),
          confidence: nameMatch[2].replace(/\s*[Cc]onfidence/i, '').trim(),
          evidence
        })
      }
    }
  }

  // Extract treatment section
  const treatmentMatch = summary.match(/7\.\s*TREATMENT RECOMMENDATIONS[\s\S]*?(?=###\s*8|$)/i)
  if (treatmentMatch) {
    // Extract immediate action
    const immediateMatch = treatmentMatch[0].match(/Immediate\s*[Aa]ction[^:]*:\s*([^\n]+)/i)
    if (immediateMatch) {
      treatment.push(immediateMatch[1].trim())
    }
    // Extract first treatment option
    const treatmentOptMatch = treatmentMatch[0].match(/(?:Antibiotics|Treatment)[^:]*:\s*([^\n]+)/i)
    if (treatmentOptMatch && treatment.length < 2) {
      treatment.push(treatmentOptMatch[1].trim())
    }
    // Extract veterinary consultation
    const vetMatch = treatmentMatch[0].match(/[Cc]onsult[^:]*[Vv]eterinarian[^:]*:\s*([^\n]+)/i)
    if (vetMatch && treatment.length < 3) {
      treatment.push('Consult veterinarian ' + vetMatch[1].trim().toLowerCase())
    }
  }

  return { diagnoses, treatment }
}

interface DiagnosisResultProps {
  result: AIAnalysisResponse
  rawContent?: string
}

const SEVERITY_CONFIG: Record<Severity, { bg: string; border: string; text: string; icon: React.ElementType; label: string; description: string }> = {
  none: {
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    text: 'text-emerald-700',
    icon: CheckCircle,
    label: 'All Clear',
    description: 'No issues detected. Fish appear healthy.'
  },
  low: {
    bg: 'bg-green-50',
    border: 'border-green-200',
    text: 'text-green-700',
    icon: CheckCircle,
    label: 'Low Severity',
    description: 'This condition is manageable and not immediately threatening.'
  },
  medium: {
    bg: 'bg-yellow-50',
    border: 'border-yellow-200',
    text: 'text-yellow-700',
    icon: AlertCircle,
    label: 'Medium Severity',
    description: 'Action recommended within the next few days.'
  },
  high: {
    bg: 'bg-orange-50',
    border: 'border-orange-200',
    text: 'text-orange-700',
    icon: AlertTriangle,
    label: 'High Severity',
    description: 'Prompt attention required to prevent spread or worsening.'
  },
  critical: {
    bg: 'bg-red-50',
    border: 'border-red-200',
    text: 'text-red-700',
    icon: XCircle,
    label: 'Critical',
    description: 'Immediate action required. This could result in significant losses if not addressed urgently.'
  },
}

export function DiagnosisResult({ result, rawContent }: DiagnosisResultProps) {
  const [showDetails, setShowDetails] = useState(false)
  const [showThinking, setShowThinking] = useState(true)
  const severity = result.severity || 'medium'
  const severityConfig = SEVERITY_CONFIG[severity]
  const SeverityIcon = severityConfig.icon

  const confidencePercent = result.confidenceScore ? Math.round(result.confidenceScore * 100) : null

  // Extract chain_of_thought from raw content
  const chainOfThought = useMemo(() => extractChainOfThought(rawContent), [rawContent])

  // Extract abstract from summary
  const abstract = useMemo(() => {
    if (result.summary) {
      return extractAbstract(result.summary)
    }
    return { diagnoses: [], treatment: [] }
  }, [result.summary])

  // Get detailed analysis fields with snake_case fallback
  const detailedAnalysis = result.detailedAnalysis || {}
  const diagnosis = detailedAnalysis.identifiedCondition || detailedAnalysis.diagnosis
  const differentialDiagnoses = detailedAnalysis.differentialDiagnosis || detailedAnalysis.differential_diagnoses || []
  const symptomsMatched = detailedAnalysis.symptoms_matched || detailedAnalysis.symptomsMatched || []
  const symptomsMissing = detailedAnalysis.symptoms_missing || detailedAnalysis.symptomsMissing || []
  const contributingFactors = detailedAnalysis.contributingFactors || []
  const treatmentProtocol = detailedAnalysis.treatmentProtocol || ''
  const preventionMeasures = detailedAnalysis.preventionMeasures || []

  return (
    <div className="space-y-5">
      {/* AI Thinking Process - Collapsible (shown first) */}
      {chainOfThought && (
        <div className="rounded-xl border border-slate-200/50 bg-gradient-to-br from-slate-50 to-white shadow-sm overflow-hidden">
          <button
            onClick={() => setShowThinking(!showThinking)}
            className="flex w-full items-center justify-between p-4 text-left hover:bg-slate-50/50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 h-8 w-8 rounded-full bg-emerald-100 flex items-center justify-center">
                <Brain className="h-4 w-4 text-emerald-600" />
              </div>
              <div>
                <span className="font-medium text-slate-900">AI Diagnostic Reasoning</span>
                <p className="text-xs text-slate-500">Step-by-step analysis process</p>
              </div>
            </div>
            {showThinking ? (
              <ChevronUp className="h-5 w-5 text-slate-400" />
            ) : (
              <ChevronDown className="h-5 w-5 text-slate-400" />
            )}
          </button>
          {showThinking && (
            <div className="border-t border-slate-100 p-5 max-h-[400px] overflow-y-auto">
              <div className="prose prose-sm prose-slate max-w-none prose-p:my-2 prose-headings:mt-4 prose-headings:mb-2 prose-ul:my-2 prose-li:my-0.5 prose-strong:text-slate-800">
                <ReactMarkdown>{chainOfThought}</ReactMarkdown>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Main Diagnosis Header - Conversational Style (shown after reasoning) */}
      <div className={`rounded-xl border-2 ${severityConfig.border} ${severityConfig.bg} overflow-hidden`}>
        <div className="p-5">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <div className={`rounded-full p-3 ${severity === 'critical' ? 'bg-red-100' : severity === 'high' ? 'bg-orange-100' : severity === 'medium' ? 'bg-yellow-100' : 'bg-green-100'}`}>
                <Stethoscope className={`h-6 w-6 ${severityConfig.text}`} />
              </div>
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 flex-wrap">
                <h3 className="text-xl font-semibold text-slate-900">
                  Diagnosis Complete
                </h3>
                <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${severityConfig.bg} ${severityConfig.text} border ${severityConfig.border}`}>
                  <SeverityIcon className="h-3.5 w-3.5" />
                  {severityConfig.label}
                </span>
              </div>
              {result.parasiteCode && (
                <p className="mt-1 text-sm text-slate-500">
                  Reference Code: <span className="font-mono bg-white/50 px-1.5 py-0.5 rounded">{result.parasiteCode}</span>
                </p>
              )}
              <p className="mt-2 text-sm text-slate-700 font-medium">
                {result.identifiedCondition || diagnosis || abstract?.diagnoses?.[0]?.name || 'Analysis complete'}
              </p>
            </div>
          </div>

          {/* Confidence Score */}
          {confidencePercent !== null && (
            <div className="mt-5 pt-4 border-t border-slate-200/50">
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-slate-600 font-medium">Diagnostic Confidence</span>
                <span className={`font-semibold ${confidencePercent >= 80 ? 'text-emerald-600' : confidencePercent >= 60 ? 'text-yellow-600' : 'text-orange-600'}`}>
                  {confidencePercent}%
                </span>
              </div>
              <div className="h-2.5 overflow-hidden rounded-full bg-white/80">
                <div
                  className={`h-full rounded-full transition-all duration-700 ease-out ${confidencePercent >= 80 ? 'bg-emerald-500' : confidencePercent >= 60 ? 'bg-yellow-500' : 'bg-orange-500'}`}
                  style={{ width: `${confidencePercent}%` }}
                />
              </div>
              <p className="mt-2 text-xs text-slate-500">
                {confidencePercent >= 80
                  ? 'High confidence - symptoms strongly match this condition.'
                  : confidencePercent >= 60
                    ? 'Moderate confidence - consider additional observations.'
                    : 'Lower confidence - further investigation recommended.'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Quick Summary Abstract - Clean vertical layout */}
      {result.summary && (abstract.diagnoses.length > 0 || abstract.treatment.length > 0) && (
        <div className="rounded-xl border border-slate-200/50 bg-gradient-to-br from-white to-slate-50/50 p-6 shadow-sm space-y-5">
          <h4 className="font-semibold text-slate-900 text-sm uppercase tracking-wide">Quick Summary</h4>

          {/* WHAT - Likely Diagnoses */}
          {abstract.diagnoses.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-emerald-700">
                <Target className="h-4 w-4 flex-shrink-0" />
                <span className="font-medium text-sm">Likely Diagnosis</span>
              </div>
              <div className="space-y-3">
                {abstract.diagnoses.slice(0, 3).map((diag, idx) => (
                  <div key={idx} className="bg-slate-50/70 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${
                        parseInt(diag.confidence) >= 70
                          ? 'bg-emerald-100 text-emerald-700'
                          : parseInt(diag.confidence) >= 50
                            ? 'bg-amber-100 text-amber-700'
                            : 'bg-slate-100 text-slate-600'
                      }`}>
                        {diag.confidence}
                      </span>
                      <span className="text-sm text-slate-900 font-semibold">{diag.name}</span>
                    </div>
                    {diag.evidence.length > 0 && (
                      <p className="text-xs text-slate-600 leading-relaxed">{diag.evidence[0]}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Divider */}
          {abstract.diagnoses.length > 0 && abstract.treatment.length > 0 && (
            <div className="border-t border-slate-200/70" />
          )}

          {/* HOW - Treatment */}
          {abstract.treatment.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-purple-700">
                <Pill className="h-4 w-4 flex-shrink-0" />
                <span className="font-medium text-sm">Recommended Actions</span>
              </div>
              <div className="space-y-2">
                {abstract.treatment.slice(0, 3).map((action, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-purple-100 text-purple-700 text-xs font-bold flex items-center justify-center mt-0.5">
                      {idx + 1}
                    </span>
                    <p className="text-sm text-slate-700 leading-relaxed">{action}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Symptoms Analysis */}
      {(symptomsMatched.length > 0 || symptomsMissing.length > 0) && (
        <div className="rounded-xl border border-slate-200/50 bg-white p-5 shadow-sm">
          <h4 className="font-medium text-slate-900 mb-4 flex items-center gap-2">
            <Lightbulb className="h-5 w-5 text-amber-500" />
            Symptom Analysis
          </h4>
          <div className="grid gap-4 md:grid-cols-2">
            {symptomsMatched.length > 0 && (
              <div className="rounded-lg bg-emerald-50/50 p-4">
                <h5 className="text-sm font-medium text-emerald-800 mb-2">Symptoms Observed</h5>
                <ul className="space-y-1.5">
                  {symptomsMatched.map((symptom: string, idx: number) => (
                    <li key={idx} className="flex items-center gap-2 text-sm text-emerald-700">
                      <CheckCircle className="h-4 w-4 flex-shrink-0" />
                      <span>{symptom}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {symptomsMissing.length > 0 && (
              <div className="rounded-lg bg-slate-50 p-4">
                <h5 className="text-sm font-medium text-slate-700 mb-2">Not Observed</h5>
                <ul className="space-y-1.5">
                  {symptomsMissing.map((symptom: string, idx: number) => (
                    <li key={idx} className="flex items-center gap-2 text-sm text-slate-500">
                      <span className="h-4 w-4 flex items-center justify-center text-slate-400">—</span>
                      <span>{symptom}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Differential Diagnoses */}
      {differentialDiagnoses.length > 0 && (
        <div className="rounded-xl border border-slate-200/50 bg-white p-5 shadow-sm">
          <h4 className="font-medium text-slate-900 mb-3">Other Possible Conditions</h4>
          <p className="text-sm text-slate-500 mb-3">
            While the primary diagnosis is most likely, these conditions should also be considered:
          </p>
          <div className="flex flex-wrap gap-2">
            {differentialDiagnoses.map((diagnosis: string, idx: number) => (
              <span
                key={idx}
                className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1.5 text-sm text-slate-700"
              >
                {diagnosis}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Detailed Analysis - Collapsible */}
      {(diagnosis || contributingFactors.length > 0 || treatmentProtocol || preventionMeasures.length > 0) && (
        <div className="rounded-xl border border-slate-200/50 bg-white shadow-sm overflow-hidden">
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="flex w-full items-center justify-between p-5 text-left hover:bg-slate-50/50 transition-colors"
          >
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-emerald-600" />
              <span className="font-medium text-slate-900">Detailed Analysis & Prevention</span>
            </div>
            {showDetails ? (
              <ChevronUp className="h-5 w-5 text-slate-400" />
            ) : (
              <ChevronDown className="h-5 w-5 text-slate-400" />
            )}
          </button>
          {showDetails && (
            <div className="border-t border-slate-100 p-5 space-y-5">
              {/* In-depth Diagnosis */}
              {diagnosis && (
                <div>
                  <h5 className="text-sm font-medium text-slate-900 mb-2">In-Depth Diagnosis</h5>
                  <p className="text-sm text-slate-600 leading-relaxed">{diagnosis}</p>
                </div>
              )}

              {/* Contributing Factors */}
              {contributingFactors.length > 0 && (
                <div>
                  <h5 className="text-sm font-medium text-slate-900 mb-2">Contributing Factors</h5>
                  <ul className="space-y-2">
                    {contributingFactors.map((factor: string, idx: number) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                        <span className="text-amber-500 mt-0.5">•</span>
                        {factor}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Treatment Protocol */}
              {treatmentProtocol && (
                <div className="rounded-lg bg-blue-50/50 p-4">
                  <h5 className="text-sm font-medium text-blue-900 mb-2 flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Treatment Protocol
                  </h5>
                  <p className="text-sm text-blue-800">{treatmentProtocol}</p>
                </div>
              )}

              {/* Prevention Measures */}
              {preventionMeasures.length > 0 && (
                <div>
                  <h5 className="text-sm font-medium text-slate-900 mb-2">Prevention for the Future</h5>
                  <ul className="space-y-2">
                    {preventionMeasures.map((measure: string, idx: number) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                        <CheckCircle className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                        {measure}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Recommendations */}
      {result.recommendations && result.recommendations.length > 0 && (
        <div>
          <h4 className="mb-3 font-medium text-slate-900 flex items-center gap-2">
            <Lightbulb className="h-5 w-5 text-amber-500" />
            Recommended Actions
          </h4>
          <RecommendationsList recommendations={result.recommendations} />
        </div>
      )}

      {/* Timeline Events */}
      {result.timelineEvents && result.timelineEvents.length > 0 && (
        <div>
          <h4 className="mb-3 font-medium text-slate-900">Related Events</h4>
          <Timeline events={result.timelineEvents} farmId={result.farmId} pondId={result.pondId} />
        </div>
      )}
    </div>
  )
}

export default DiagnosisResult
